// Shared textarea auto-resize utilities.
//
// Patient and informant forms use auto-expanding textareas for free-text
// answers.  The original implementation performed synchronous layout reads
// (scrollHeight) inside onInput handlers, which forces the browser to
// recalculate layout mid-keystroke.  On mobile devices this creates
// perceptible input lag.
//
// These helpers defer the resize to the next animation frame, batching
// the layout work with the browser's regular paint cycle.  A per-element
// guard (_resizeRaf) ensures at most one resize is queued per frame, even
// when multiple input events fire between frames (common during IME
// composition on mobile or fast desktop typing).

const MAX_TEXTAREA_HEIGHT = 200;

/**
 * Queue a textarea height recalculation for the next animation frame.
 *
 * Call this from an onInput handler:
 *   onInput={(e) => resizeTextarea(e.target)}
 *
 * @param {HTMLTextAreaElement|null} el  - The textarea element to resize
 * @param {number} [maxHeight=200]      - Maximum height in pixels
 */
export function resizeTextarea(el, maxHeight = MAX_TEXTAREA_HEIGHT) {
  if (!el || el._resizeRaf) return;
  el._resizeRaf = requestAnimationFrame(() => {
    el._resizeRaf = null;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
  });
}

/**
 * Build a ref callback that combines a React Hook Form ref with a
 * one-shot rAF resize for pre-filled content.
 *
 * When a user navigates back to a previously answered question, the
 * textarea mounts with existing content but its intrinsic height is
 * only one or two rows.  This callback recalculates the height on
 * mount so the full content is visible without scrolling.
 *
 * Usage with register():
 *   const { ref } = register(answerKey);
 *   <textarea ref={createTextareaRefCallback(ref)} ... />
 *
 * @param {Function|Object} rhfRef      - The ref from register()
 * @param {number}          [maxHeight] - Maximum height in pixels
 * @returns {Function} A ref callback suitable for the textarea's ref prop
 */
export function createTextareaRefCallback(rhfRef, maxHeight = MAX_TEXTAREA_HEIGHT) {
  return (el) => {
    // Defensive try/catch: React Hook Form's internal ref callback can throw
    // "Cannot read properties of undefined (reading '_f')" when the register
    // key contains characters that cause RHF's set() and get() utilities to
    // disagree on the storage key (e.g. commas + apostrophes). The primary
    // fix is sanitizing keys via sanitizeOptionForKey, but this guard ensures
    // the page still renders even if a future key slips through.
    if (typeof rhfRef === 'function') {
      try {
        rhfRef(el);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[createTextareaRefCallback] RHF ref callback failed:', err);
        }
      }
    }
    if (el) {
      requestAnimationFrame(() => {
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
      });
    }
  };
}
